// Client mirror of the server's placeholder write gate for placeholder-bearing
// CMS fields (service.ValidateSiteContentOverlay + scanContentPlaceholders in
// internal/service/content_field_placeholders.go — itself the grammar of
// studio-sites booking-message.ts). Same tokenizer, same precedence (unknown
// names before stray braces), so a value this accepts the server accepts too.
// The server gate stays authoritative; this only keeps half-typed tokens
// ("{gue") out of the autosave so they show an inline error instead of a
// rejected PUT + toast per typing pause.

export type PlaceholderProblem = { kind: "unknown"; names: string[] } | { kind: "stray" } | null;

// The field's message templates, server-rendered from the errors.content.*
// keys; UNKNOWN_SENTINEL stands in for the offending {name} list.
export const UNKNOWN_SENTINEL = "@@PLACEHOLDERS@@";

export interface PlaceholderMessages {
  unknown: string;
  stray: string;
}

export function checkContentPlaceholders(text: string, allowed: readonly string[]): PlaceholderProblem {
  const ok = new Set(allowed);
  const re = /\{([^{}]*)\}/g;
  const bad: string[] = [];
  for (const m of text.matchAll(re)) {
    const name = m[1];
    if (!ok.has(name) && !bad.includes(name)) bad.push(name);
  }
  if (bad.length > 0) return { kind: "unknown", names: bad };
  const rest = text.replace(re, "");
  if (rest.includes("{") || rest.includes("}")) return { kind: "stray" };
  return null;
}

export function placeholderErrorText(problem: PlaceholderProblem, msgs: PlaceholderMessages): string {
  if (!problem) return "";
  if (problem.kind === "stray") return msgs.stray;
  return msgs.unknown.split(UNKNOWN_SENTINEL).join(problem.names.map((n) => `{${n}}`).join(", "));
}

// parseAllowed reads a field's data-cms-placeholders JSON list; [] when absent
// or malformed (the field is then not placeholder-gated client-side).
export function parseAllowed(raw: string | undefined): string[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

// insertPlaceholderToken inserts {name} at the caret (replacing a selection)
// and fires a bubbling input so the editor's normal autosave path runs —
// mirrors the guest-email texts page (guestEmailsInsertPlaceholderJS).
export function insertPlaceholderToken(el: HTMLInputElement | HTMLTextAreaElement, name: string): void {
  const p = el.selectionStart ?? el.value.length;
  el.setRangeText(`{${name}}`, p, el.selectionEnd ?? p, "end");
  el.dispatchEvent(new Event("input", { bubbles: true }));
  el.focus();
}

// renderPlaceholderError shows/clears the field's inline error element (wired
// via aria-describedby) and aria-invalid. Returns the problem.
export function renderPlaceholderError(el: HTMLElement): PlaceholderProblem {
  const allowed = parseAllowed(el.dataset.cmsPlaceholders);
  const value = (el as HTMLInputElement).value ?? "";
  const problem = checkContentPlaceholders(value, allowed);
  const errId = el.dataset.cmsPlaceholderErrorId;
  const errEl = errId ? document.getElementById(errId) : null;
  if (errEl) {
    const text = placeholderErrorText(problem, {
      unknown: errEl.dataset.msgUnknown || "",
      stray: errEl.dataset.msgStray || "",
    });
    errEl.textContent = text;
    errEl.hidden = !problem;
  }
  if (problem) el.setAttribute("aria-invalid", "true");
  else el.removeAttribute("aria-invalid");
  return problem;
}
